import { FormErrors, UseFormReturnType } from "@mantine/form";
import { LooseKeys, OnSubmit, _TransformValues } from "@mantine/form/lib/types";
import { IFocusable, IFocusableForm } from "./Form.types";

export class AutoFocusableForm<
  Values = Record<string, unknown>,
  TransformValues extends _TransformValues<Values> = (values: Values) => Values,
> implements IFocusableForm<Values, TransformValues>
{
  private form: UseFormReturnType<Values, TransformValues>;
  private fieldRefs: {
    [key in LooseKeys<Values>]?: React.RefObject<IFocusable>;
  } = {};

  constructor(form: UseFormReturnType<Values, TransformValues>) {
    this.form = form;
  }

  // New methods

  public registerField = (
    fieldName: LooseKeys<Values>,
    ref: React.RefObject<IFocusable>,
  ) => {
    this.fieldRefs[fieldName] = ref;
  };

  // Wrappers for existing methods

  public getValues = () => {
    return this.form.values;
  };

  public getErrors = () => {
    return this.form.errors;
  };

  public isValid = <Field extends LooseKeys<Values>>(path?: Field) => {
    return this.form.isValid(path);
  };
  public validate = () => {
    return this.form.validate();
  };

  public setValues = (values: React.SetStateAction<Partial<Values>>) => {
    this.form.setValues(values);
  };

  public setFieldValue = <Field extends LooseKeys<Values>>(
    path: Field,
    value: Field extends keyof Values ? Values[Field] : unknown,
  ) => {
    return this.form.setFieldValue(path, value);
  };

  public setErrors = (errors: React.SetStateAction<FormErrors>) => {
    if (errors) {
      const errorKeys = Object.keys(errors);
      if (errorKeys.length > 0) {
        const firstRef = (this.fieldRefs as any)[errorKeys[0]];
        if (firstRef?.current) {
          firstRef.current.focus();
        }
      }
    }
    return this.form.setErrors(errors);
  };

  public reset = () => {
    this.form.reset();
  };

  public resetDirty = (values?: Values) => {
    this.form.resetDirty(values);
  };

  public isDirty = <Field extends LooseKeys<Values>>(path?: Field) => {
    return this.form.isDirty(path);
  };

  public getInputProps = (
    fieldName: LooseKeys<Values>,
    options?: {
      type?: "input" | "checkbox";
      withError?: boolean;
      withFocus?: boolean;
    },
  ) => {
    return this.form.getInputProps(fieldName, options);
  };

  public onSubmit: OnSubmit<Values, TransformValues> = (
    handleSubmit: (
      values: ReturnType<TransformValues>,
      event: React.FormEvent<HTMLFormElement>,
    ) => void,
    handleValidationFailure?: (
      errors: FormErrors,
      values: Values,
      event: React.FormEvent<HTMLFormElement>,
    ) => void,
  ) => {
    return this.form.onSubmit(handleSubmit, handleValidationFailure);
  };

  public insertListItem = <Field extends LooseKeys<Values>>(
    path: Field,
    item: unknown,
    index?: number,
  ) => {
    this.form.insertListItem(path, item, index);
  };

  public removeListItem = <Field extends LooseKeys<Values>>(
    path: Field,
    index: number,
  ) => {
    this.form.removeListItem(path, index);
  };
}
